/**
* Message bubble component for displaying messages
*/

import type { WidgetMessage, WidgetButton, WidgetPoll } from '../types'
import type { ThemePalette } from '../utils/themeUtils'
import { getFileIcon } from '../utils/fileUtils'
import { linkifyText } from '../utils/linkUtils'

interface MessageBubbleProps {
    message: WidgetMessage
    palette: ThemePalette
    onPollSelect?: (option: string, poll?: WidgetPoll) => void
    onButtonClick?: (button: WidgetButton) => void
    selectedOption?: string
}

export function MessageBubble({
    message,
    palette,
    onPollSelect,
    onButtonClick,
    selectedOption
}: MessageBubbleProps) {
    const hasPoll = message.interactivePayload?.type === 'poll' && message.interactivePayload?.poll
    const hasButtons = message.interactivePayload?.type === 'buttons' && message.interactivePayload?.buttons?.length

    return (
        <div
            style={{
                alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '82%',
                padding: '10px 14px',
                borderRadius:
                    message.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: message.sender === 'user' ? palette.userBubble : palette.assistantBubble,
                color: message.sender === 'user' ? '#ffffff' : palette.textPrimary,
                // Ensure text is readable in dark theme assistant bubbles
                fontWeight: message.sender === 'user' ? 400 : 400,
                boxShadow:
                    message.sender === 'user'
                        ? '0 12px 24px rgba(99,102,241,0.35)'
                        : '0 6px 20px rgba(15,23,42,0.12)',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
                letterSpacing: '-0.01em',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
            }}
        >
            {/* Image display */}
            {message.type === 'image' && message.downloadUrl && (
                <img
                    src={message.downloadUrl}
                    alt="Sent image"
                    style={{
                        width: '100%',
                        maxWidth: '300px',
                        height: 'auto',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        objectFit: 'cover',
                    }}
                    onClick={() => {
                        window.open(message.downloadUrl, '_blank')
                    }}
                />
            )}

            {/* Video display */}
            {message.type === 'video' && message.downloadUrl && (
                <video
                    src={message.downloadUrl}
                    controls
                    style={{
                        width: '100%',
                        maxWidth: '300px',
                        height: 'auto',
                        borderRadius: '8px',
                    }}
                />
            )}

            {/* Audio display */}
            {message.type === 'audio' && message.downloadUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <audio
                        src={message.downloadUrl}
                        controls
                        style={{
                            flex: 1,
                            maxWidth: '250px',
                        }}
                    />
                </div>
            )}

            {/* Document/File display */}
            {(message.type === 'document' || message.type === 'file') && message.downloadUrl && (
                <div
                    onClick={() => window.open(message.downloadUrl, '_blank')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: message.sender === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.8'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1'
                    }}
                >
                    <span style={{ fontSize: '32px' }}>{getFileIcon(message.mimeType, message.type)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {message.mediaFileName || 'File'}
                        </div>
                        {message.mimeType && (
                            <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
                                {message.mimeType.split('/')[1]?.toUpperCase() || 'FILE'}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Text content with clickable links */}
            {message.text && (
                <div>{linkifyText(message.text, palette)}</div>
            )}

            {hasPoll && message.interactivePayload?.poll && (
                <PollDisplay
                    poll={message.interactivePayload.poll}
                    onSelect={(option) => onPollSelect?.(option, message.interactivePayload?.poll)}
                    selectedOption={selectedOption}
                    palette={palette}
                />
            )}

            {/* Buttons display */}
            {hasButtons && message.interactivePayload?.buttons && (
                <ButtonsDisplay
                    buttons={message.interactivePayload.buttons}
                    onButtonClick={onButtonClick}
                    selectedOption={selectedOption}
                    palette={palette}
                />
            )}

            {/* Caption for media files */}
            {(message.type === 'image' || message.type === 'video') && message.caption && !message.text && (
                <div style={{ fontSize: '13px', opacity: 0.9 }}>
                    {message.caption}
                </div>
            )}
        </div>
    )
}

// Poll display component (inline)
function PollDisplay({
    poll,
    onSelect,
    selectedOption,
    palette
}: {
    poll: WidgetPoll
    onSelect?: (option: string) => void
    selectedOption?: string
    palette: ThemePalette
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            {poll.pollName && (
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                    {poll.pollName}
                </div>
            )}
            {poll.pollOptions.map((option, index) => {
                const isSelected = selectedOption === option
                return (
                    <button
                        key={index}
                        onClick={() => onSelect?.(option)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            border: `1px solid ${isSelected ? '#22c55e' : palette.border}`,
                            borderRadius: '10px',
                            background: isSelected ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '14px',
                            color: palette.textPrimary,
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {/* Radio circle */}
                        <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            border: `2px solid ${isSelected ? '#22c55e' : palette.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            {isSelected && (
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: '#22c55e',
                                }} />
                            )}
                        </div>
                        <span>{option}</span>
                    </button>
                )
            })}
        </div>
    )
}

// Buttons display component (inline)
function ButtonsDisplay({
    buttons,
    onButtonClick,
    selectedOption,
    palette
}: {
    buttons: WidgetButton[]
    onButtonClick?: (button: WidgetButton) => void
    selectedOption?: string
    palette: ThemePalette
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            {buttons.map((button) => {
                const isSelected = selectedOption === button.displayText
                const isUrl = button.type === 'cta_url' && button.url

                return (
                    <button
                        key={button.id}
                        onClick={() => {
                            if (isUrl) {
                                window.open(button.url, '_blank')
                            } else {
                                onButtonClick?.(button)
                            }
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '10px 16px',
                            border: 'none',
                            borderRadius: '10px',
                            background: isSelected
                                ? '#22c55e'
                                : selectedOption
                                    ? 'rgba(156, 163, 175, 0.3)'
                                    : palette.gradient,
                            color: isSelected ? '#ffffff' : (selectedOption ? palette.textSecondary : '#ffffff'),
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            boxShadow: isSelected
                                ? '0 4px 12px rgba(34, 197, 94, 0.3)'
                                : '0 4px 12px rgba(99, 102, 241, 0.2)',
                        }}
                    >
                        {isSelected && <span>✓</span>}
                        <span>{button.displayText}</span>
                        {isUrl && <span style={{ fontSize: '12px' }}>↗</span>}
                    </button>
                )
            })}
        </div>
    )
}
